import React, { useState, useEffect } from 'react';
import { Button, message, Spin, Modal, Checkbox, List, Pagination, Row, Col, Card, Typography, Space, Tag, Input, Switch } from 'antd';
import { SearchOutlined, EyeOutlined, ImportOutlined, DownloadOutlined, UploadOutlined, SettingOutlined, TagOutlined, TeamOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/page-headers/page-headers';
import { Main } from '../styled';
import useFormLibraryList from '../../api/useFormLibraryList';
import useFormLibrarySearch from '../../api/useFormLibrarySearch';
import useFormLibraryImport from '../../api/useFormLibraryImport';
import useFormLibraryUploadCsv from '../../api/useFormLibraryUploadCsv';
import useFormLibraryAdd from '../../api/useFormLibraryAdd';
import useFormLibraryVisibility from '../../api/useFormLibraryVisibility';
import useFormLibraryClientAccess from '../../api/useFormLibraryClientAccess';
import useFormLibraryUpdate from '../../api/useFormLibraryUpdate';
import useClientList from '../../api/useClientList';
import FormLibraryCsvUpload from '../../components/formLibrary/FormLibraryCsvUpload';
import './FormLibrary.css';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const FormLibrary = () => {
  const { user } = useAuth();
  const userRole = user?.roles;

  const { response: libraryList, isLoading: libraryLoading, fetchFormLibraryList } = useFormLibraryList();
  const { response: searchResponse, isLoading: searchLoading, searchFormLibrary } = useFormLibrarySearch();
  const { response: importResponse, error: importError, isLoading: importLoading, importFormForClient } = useFormLibraryImport();
  const { response: uploadResponse, error: uploadError, isLoading: uploadLoading, uploadCsvToLibrary, reset: resetUpload } = useFormLibraryUploadCsv();
  const { response: addResponse, error: addError, isLoading: addLoading, addFormToLibrary, reset: resetAdd } = useFormLibraryAdd();
  const { updateVisibility } = useFormLibraryVisibility();
  const {
    loading: clientAccessLoading,
    getClientAccess,
    addClientAccess,
    removeClientAccess,
    response: clientAccessResponse,
  } = useFormLibraryClientAccess();
  const { loading: clientListLoading, response: clientListResponse, fetchClients } = useClientList();
  const { response: updateResponse, error: updateError, isLoading: updateLoading, updateFormLibrary, reset: resetUpdate } = useFormLibraryUpdate();

  // Modal and state for managing client access
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessModalForm, setAccessModalForm] = useState(null); // form_library_id
  const [accessClients, setAccessClients] = useState([]); // client ids with access
  const [selectedClients, setSelectedClients] = useState([]); // checked client ids
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize] = useState(10); // Remove setClientPageSize (unused)
  const [accessModalLoading, setAccessModalLoading] = useState(false);
  const [readyToOpenModal, setReadyToOpenModal] = useState(false);
  const [formDetailsModal, setFormDetailsModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const [librarySkip, setLibrarySkip] = useState(0);
  const [libraryLimit, setLibraryLimit] = useState(12);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [shouldSearch, setShouldSearch] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'csv' or 'add'
  const [modalInitialData, setModalInitialData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentResponse, setCurrentResponse] = useState(null);
  const [currentError, setCurrentError] = useState(null);

  // Add edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editInitialData, setEditInitialData] = useState(null);
  const [editFormId, setEditFormId] = useState(null);

  // Add state for client search in access modal
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchInput, setClientSearchInput] = useState('');


  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setModalMode('csv');
      setModalInitialData(null);
      setModalOpen(true);
    }
    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) {
      fileInput.click();
    }
  };



  const closeModal = () => {
    setModalOpen(false);
    setModalInitialData(null);
    setModalMode(null);
    setSelectedFile(null);
    setCurrentResponse(null);
    setCurrentError(null);
    // Reset API states when modal closes
    resetUpload();
    resetAdd();
  };

  // Handle search trigger
  useEffect(() => {
    if (shouldSearch && isSearchMode && searchQuery.trim()) {
      searchFormLibrary({ query: searchQuery, skip: librarySkip, limit: libraryLimit });
      setActiveSearchQuery(searchQuery); // Set the active search query when search is executed
      setShouldSearch(false); // Reset the flag after triggering search
    }
  }, [shouldSearch, isSearchMode, searchQuery, librarySkip, libraryLimit]);

  // Handle pagination changes
  useEffect(() => {
    if (isSearchMode) {
      // Use the current searchQuery for pagination in search mode
      searchFormLibrary({ query: searchQuery, skip: librarySkip, limit: libraryLimit });
    } else {
      fetchFormLibraryList({ skip: librarySkip, limit: libraryLimit });
    }
  }, [librarySkip, libraryLimit, isSearchMode]);

  // Handle modal form submission
  const handleModalSubmit = async (data) => {
    try {
      if (data.mode === 'csv') {
        // Handle CSV upload
        await uploadCsvToLibrary({ formData: data.formData });
      } else if (data.mode === 'add') {
        // Handle add to library
        await addFormToLibrary({ data: data.data });
      }
    } catch (error) {
      console.error('Modal submit error:', error);
    }
  };

  // Handle upload response and error states
  useEffect(() => {
    if (uploadLoading) {
      message.loading({ content: 'Uploading CSV...', key: 'uploading', duration: 0 });
    } else if (uploadResponse) {
      console.log('Upload Response:', uploadResponse);
      // Check if the response contains error information
      const hasError = uploadResponse.error || uploadResponse.errors || uploadResponse.status === 'error' || 
                      (uploadResponse.data && (uploadResponse.data.error || uploadResponse.data.errors));
      
      console.log('Has Error:', hasError);
      
      if (hasError) {
        // Response contains error - set as error for modal
        setCurrentError(uploadResponse);
        setCurrentResponse(null);
        // Dismiss loading message since there's an error
        message.destroy('uploading');
      } else {
        // Success case
        let successMsg = 'CSV uploaded to library successfully!';
        if (uploadResponse?.message) successMsg = uploadResponse.message;
        else if (uploadResponse?.data?.message) successMsg = uploadResponse.data.message;
        else if (uploadResponse?.detail) successMsg = uploadResponse.detail;
        message.success({ content: successMsg, key: 'uploading' });
        // Set current response for modal
        setCurrentResponse(uploadResponse);
        setCurrentError(null);
        // Refresh the library list after successful upload
        fetchFormLibraryList({ skip: librarySkip, limit: libraryLimit });
      }
    } else if (uploadError) {
      // Set current error for modal
      setCurrentError(uploadError);
      setCurrentResponse(null);
      // Dismiss loading message since there's an error
      message.destroy('uploading');
    }
  }, [uploadLoading, uploadResponse, uploadError, librarySkip, libraryLimit]);

  // Handle add response and error states
  useEffect(() => {
    if (addLoading) {
      message.loading({ content: 'Adding to library...', key: 'adding', duration: 0 });
    } else if (addResponse) {
      console.log('Add Response:', addResponse);
      // Check if the response contains error information
      const hasError = addResponse.error || addResponse.errors || addResponse.status === 'error' || 
                      (addResponse.data && (addResponse.data.error || addResponse.data.errors));
      
      console.log('Has Error:', hasError);
      
      if (hasError) {
        // Response contains error - set as error for modal
        setCurrentError(addResponse);
        setCurrentResponse(null);
        // Dismiss loading message since there's an error
        message.destroy('adding');
      } else {
        // Success case
        let successMsg = 'Form added to library successfully!';
        if (addResponse?.message) successMsg = addResponse.message;
        else if (addResponse?.data?.message) successMsg = addResponse.data.message;
        else if (addResponse?.detail) successMsg = addResponse.detail;
        message.success({ content: successMsg, key: 'adding' });
        // Set current response for modal
        setCurrentResponse(addResponse);
        setCurrentError(null);
        // Refresh the library list after successful add
        fetchFormLibraryList({ skip: librarySkip, limit: libraryLimit });
      }
    } else if (addError) {
      // Set current error for modal
      setCurrentError(addError);
      setCurrentResponse(null);
      // Dismiss loading message since there's an error
      message.destroy('adding');
    }
  }, [addLoading, addResponse, addError, librarySkip, libraryLimit]);

  useEffect(() => {
    if (importLoading) {
      message.loading({ content: 'Importing form...', key: 'importing', duration: 0 });
    } else if (importResponse) {
      let successMsg = 'Form imported successfully!';
      if (importResponse?.message) successMsg = importResponse.message;
      else if (importResponse?.data?.message) successMsg = importResponse.data.message;
      else if (importResponse?.detail) successMsg = importResponse.detail;
      message.success({ content: successMsg, key: 'importing' });
    } else if (importError) {
      let errorMsg = 'Failed to import form.';
      if (importError?.response?.data?.detail) errorMsg = importError.response.data.detail;
      else if (importError?.response?.data?.message) errorMsg = importError.response.data.message;
      else if (importError?.response?.message) errorMsg = importError.response.message;
      else if (importError?.message) errorMsg = importError.message;
      else if (typeof importError === 'string') errorMsg = importError;
      message.error({ content: errorMsg, key: 'importing' });
    }
  }, [importLoading, importResponse, importError]);

  // Handler for search input change
  const handleClientSearchInputChange = (e) => {
    setClientSearchInput(e.target.value);
  };

  // Handler for search submit
  const handleClientSearch = () => {
    setClientPage(1);
    setClientSearch(clientSearchInput.trim());
    fetchClients({ skip: 0, limit: clientPageSize, search: clientSearchInput.trim() });
  };

  // Handler for pressing Enter in search input
  const handleClientSearchInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleClientSearch();
    }
  };

  // Only fetch clients on page change or modal open, not on every search input change
  useEffect(() => {
    if (accessModalOpen) {
      fetchClients({ skip: (clientPage - 1) * clientPageSize, limit: clientPageSize, search: clientSearch });
    }
  }, [accessModalOpen, clientPage, clientPageSize, clientSearch]);

  // Update accessClients from hook response
  useEffect(() => {
    if (clientAccessResponse && Array.isArray(clientAccessResponse)) {
      setAccessClients(clientAccessResponse.map(c => c.client_id));
      setSelectedClients(clientAccessResponse.map(c => c.client_id));
    }
  }, [clientAccessResponse]);

  // When opening the modal, reset search and page
  const openAccessModal = async (record) => {
    setAccessModalLoading(true);
    setAccessModalForm(record.id);
    setClientSearch('');
    setClientSearchInput('');
    setClientPage(1);
    await fetchClients({ skip: 0, limit: clientPageSize, search: '' });
    await getClientAccess({ form_library_id: record.id });
    setAccessModalLoading(false);
    setReadyToOpenModal(true);
  };

  // Handle visibility toggle
  const handleVisibilityChange = async (checked, record) => {
    // If toggling from public to private, first update visibility then open manage access modal
    if (!checked && record.is_public) {
      // First update visibility to private
      await updateVisibility({
        form_library_id: record.id,
        is_public: false,
      });
      
      // Refresh the form library list to update UI
      fetchFormLibraryList({ skip: librarySkip, limit: libraryLimit });
      
      // Then open manage access modal
      await openAccessModal(record);
      return;
    }
    
    // Otherwise, update visibility directly
    await updateVisibility({
      form_library_id: record.id,
      is_public: checked,
    });
    fetchFormLibraryList({ skip: librarySkip, limit: libraryLimit });
  };

  useEffect(() => {
    if (readyToOpenModal && selectedClients.length >= 0) {
      setAccessModalOpen(true);
      setReadyToOpenModal(false);
    }
  }, [readyToOpenModal, selectedClients]);

  // Handle client checkbox change
  const handleClientCheck = (clientId, checked) => {
    const newSelected = checked
      ? [...selectedClients, clientId]
      : selectedClients.filter(id => id !== clientId);
    setSelectedClients(newSelected);
  };

  // Save client access changes (avoid await in loop)
  const handleSaveClientAccess = async () => {
    // Add new clients
    const addPromises = selectedClients
      .filter(clientId => !accessClients.includes(clientId))
      .map(clientId => addClientAccess({ form_library_id: accessModalForm, client_id: clientId }));
    // Remove unchecked clients
    const removePromises = accessClients
      .filter(clientId => !selectedClients.includes(clientId))
      .map(clientId => removeClientAccess({ form_library_id: accessModalForm, client_id: clientId }));
    await Promise.all([...addPromises, ...removePromises]);
    await getClientAccess({ form_library_id: accessModalForm });
    setAccessModalOpen(false);
  };

  // Map data to ensure correct fields for name/description
  const apiResponse = isSearchMode ? searchResponse : libraryList;
  const libraryDataRaw = Array.isArray(apiResponse?.data)
    ? apiResponse.data
    : Array.isArray(apiResponse) ? apiResponse : apiResponse?.forms || [];
  const libraryData = libraryDataRaw.map(item => {
    console.log('Form item duration:', item.duration, 'for form:', item.form_name || item.name);
    return {
      ...item,
      form_name: item.form_name || item.name || '',
      form_description: item.form_description || item.description || '',
      visibility: item.is_public ? 'public' : 'private',
      credit_cost: item.credit_cost !== undefined ? item.credit_cost : 0,
      duration: item.duration !== undefined ? item.duration : 0,
      thumbnail: item.thumbnail || '',
      extra_metadata: item.extra_metadata || {}
    };
  });
  const totalCount = apiResponse?.total_count || libraryDataRaw.length;

  const handleImport = (record) => {
    importFormForClient({ data: { form_library_id: record.id } });
  };

  const openFormDetails = (record) => {
    setSelectedForm(record);
    setFormDetailsModal(true);
  };

  const closeFormDetails = () => {
    setFormDetailsModal(false);
    setSelectedForm(null);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleSearchSubmit = (value) => {
    const query = value || searchQuery;
    if (query.trim()) {
      setIsSearchMode(true);
      setSearchQuery(query);
      setLibrarySkip(0);
      setShouldSearch(true); // Trigger the search
    } else {
      setIsSearchMode(false);
      setLibrarySkip(0);
    }
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setIsSearchMode(false);
    setLibrarySkip(0);
    setShouldSearch(false);
  };

  // Open edit modal with form data
  const openEditModal = (record) => {
    setEditInitialData(record);
    setEditFormId(record.id);
    setEditModalOpen(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditInitialData(null);
    setEditFormId(null);
    resetUpdate();
  };

  // Handle edit modal submit
  const handleEditModalSubmit = async (data) => {
    try {
      if (data.mode === 'edit') {
        await updateFormLibrary({ id: editFormId, data: data.data });
      }
    } catch (error) {
      console.error('Edit modal submit error:', error);
    }
  };

  // Handle update response and error
  useEffect(() => {
    if (updateLoading) {
      message.loading({ content: 'Updating form...', key: 'updating', duration: 0 });
    } else if (updateResponse) {
      let successMsg = 'Form updated successfully!';
      if (updateResponse?.message) successMsg = updateResponse.message;
      else if (updateResponse?.data?.message) successMsg = updateResponse.data.message;
      else if (updateResponse?.detail) successMsg = updateResponse.detail;
      message.success({ content: successMsg, key: 'updating' });
      closeEditModal();
      fetchFormLibraryList({ skip: librarySkip, limit: libraryLimit });
    } else if (updateError) {
      let errorMsg = 'Failed to update form.';
      if (updateError?.response?.data?.detail) errorMsg = updateError.response.data.detail;
      else if (updateError?.response?.data?.message) errorMsg = updateError.response.data.message;
      else if (updateError?.response?.message) errorMsg = updateError.response.message;
      else if (updateError?.message) errorMsg = updateError.message;
      else if (typeof updateError === 'string') errorMsg = updateError;
      message.error({ content: errorMsg, key: 'updating' });
    }
  }, [updateLoading, updateResponse, updateError, librarySkip, libraryLimit]);

  // Helper function to render form card with modern design
  const renderFormCard = (record) => {
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={record.id}>
        <Card
          className="form-card"
          bodyStyle={{ padding: 0 }}
          style={{ cursor: 'pointer' }}
          onClick={() => openFormDetails(record)}
        >
          {/* Visibility Badge */}
          <div className={`visibility-badge ${record.is_public ? 'visibility-badge-public' : 'visibility-badge-private'}`}>
            {record.is_public ? 'Public' : 'Private'}
          </div>

          {/* Header Section */}
          <div className="form-card-header">
            <div className="form-card-title">
              {record.form_name}
            </div>
            <div className="form-card-description">
              {record.form_description || 'No description available'}
            </div>
            {/* Credit Cost and Duration Display */}
            <div className="form-card-credit-cost">
              <span style={{ 
                color: record.credit_cost > 0 ? '#52c41a' : '#999', 
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                💰 {record.credit_cost} credits
              </span>
              {console.log('Rendering duration for form:', record.form_name, 'duration:', record.duration, 'type:', typeof record.duration)}
              {record.duration > 0 && (
                <span style={{ 
                  color: '#1890ff', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginLeft: '12px'
                }}>
                  ⏱️ {record.duration} min
                </span>
              )}
            </div>
          </div>

          {/* Body Section */}
          <div className="form-card-body">
            {/* Thumbnail Section */}
            {record.thumbnail && record.thumbnail.trim() !== '' && (
              <div className="form-card-thumbnail">
                <img 
                  src={record.thumbnail} 
                  alt="Form thumbnail" 
                  style={{ 
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }} 
                  onError={(e) => {
                    console.log('Thumbnail failed to load:', record.thumbnail);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('Thumbnail loaded successfully:', record.thumbnail)}
                />
              </div>
            )}


            
            {/* Tags Section */}
            <div className="form-card-tags-section">
              {/* Form Type and Status */}
              <div className="form-card-tags-row">
                {record.form_type && (
                  <span className="form-card-tag form-card-tag-primary">
                    <TagOutlined style={{ marginRight: '4px' }} />
                    {record.form_type}
                  </span>
                )}
                {record.status && (
                  <span className={`form-card-tag ${record.status === 'active' ? 'form-card-tag-success' : 'form-card-tag-warning'}`}>
                    {record.status}
                  </span>
                )}
              </div>
              
              {/* Target Audience Tags */}
              {record.target_audience && record.target_audience.length > 0 && (
                <div className="form-card-tags-row">
                  <div className="form-card-section-title">
                    <TeamOutlined />
                    Target Audience
                  </div>
                  <div>
                    {record.target_audience.slice(0, 3).map((audience, index) => (
                      <span key={index} className="form-card-tag form-card-tag-purple">
                        {audience}
                      </span>
                    ))}
                    {record.target_audience.length > 3 && (
                      <span className="form-card-tag form-card-tag-default">
                        +{record.target_audience.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {record.tags && record.tags.length > 0 && (
                <div className="form-card-tags-row">
                  <div className="form-card-section-title">
                    <TagOutlined />
                    Tags
                  </div>
                  <div>
                    {record.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="form-card-tag form-card-tag-primary">
                        {tag}
                      </span>
                    ))}
                    {record.tags.length > 3 && (
                      <span className="form-card-tag form-card-tag-default">
                        +{record.tags.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}


            </div>

            {/* Action Buttons */}
            <div className="form-card-actions">
              <Row gutter={[8, 8]} justify="center">
                <Col>
                  <Button 
                    type="primary" 
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openFormDetails(record);
                    }}
                    className="form-card-button form-card-button-primary"
                    style={{ minWidth: 90, fontSize: '13px', height: '32px', padding: '0 10px' }}
                  >
                    View Details
                  </Button>
                </Col>
                <Col>
                  <Button 
                    size="small"
                    icon={<ImportOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImport(record);
                    }}
                    className="form-card-button form-card-button-default"
                    style={{ minWidth: 70, fontSize: '13px', height: '32px', padding: '0 10px' }}
                  >
                    Import
                  </Button>
                </Col>
                {userRole === 'superadmin' && (
                  <Col>
                    <Button
                      size="small"
                      icon={<SettingOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(record);
                      }}
                      className="form-card-button form-card-button-default"
                      style={{ minWidth: 60, fontSize: '13px', height: '32px', padding: '0 10px' }}
                    >
                      Edit
                    </Button>
                  </Col>
                )}
              </Row>
              
              {/* Visibility Toggle Switch */}
              {userRole === 'superadmin' && (
                <Row style={{ marginTop: '12px' }}>
                  <Col span={24}>
                    <div 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e1e5e9'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {record.is_public ? 'Public' : 'Private'}
                      </span>
                      <Switch
                        checked={record.is_public}
                        onChange={(checked) => {
                          handleVisibilityChange(checked, record);
                        }}
                        size="small"
                      />
                    </div>
                  </Col>
                </Row>
              )}
              
              {userRole === 'superadmin' && record.visibility === 'private' && (
                <Row style={{ marginTop: '8px' }}>
                  <Col span={24}>
                    <Button 
                      size="small"
                      icon={<SettingOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openAccessModal(record);
                      }}
                      className="form-card-button form-card-button-default"
                      style={{ width: '100%', fontSize: '12px', height: '32px', padding: '0 10px' }}
                    >
                      Manage Access
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <PageHeader ghost title="Assessment Library" />
      <Main className="form-library-container">
        {/* Search and Upload Section */}
        <div className="search-container" style={{ 
          padding: '24px 0', 
          marginBottom: '24px',
          backgroundColor: 'transparent'
        }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={userRole === 'superadmin' ? 18 : 24}>
              <div style={{ 
                display: 'flex', 
                height: '44px',
                borderRadius: '8px',
                border: '1px solid #e1e5e9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: '#f8f9fa',
                overflow: 'hidden'
              }}>
                <Input
                  placeholder="Search forms by name, description, tags, or target audience..."
                  allowClear
                  size="large"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onPressEnter={() => handleSearchSubmit()}
                  style={{ 
                    border: 'none',
                    boxShadow: 'none',
                    flex: 1,
                    height: '100%',
                    backgroundColor: 'transparent'
                  }}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => handleSearchSubmit()}
                  style={{ 
                    border: 'none',
                    borderRadius: '0',
                    height: '100%',
                    width: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </div>
            </Col>
            {userRole === 'superadmin' && (
              <Col xs={24} md={6}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Button
                    type="primary"
                    onClick={triggerFileInput}
                    size="small"
                    // icon={<ImportOutlined />}
                    icon={<UploadOutlined />}
                    style={{ 
                      width: '100%',
                      height: '36px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: 'none',
                      fontWeight: '500',
                      backgroundColor: '#1890ff',
                      fontSize: '13px',
                      padding: '0 12px'
                    }}
                  >
                    Upload CSV
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    style={{ width: '100%', height: '36px', borderRadius: '8px', fontWeight: '500', fontSize: '13px', padding: '0 12px' }}
                    onClick={() => {
                      window.open('https://stride-survey-assets.s3.amazonaws.com/sample-csv/sample-csv.csv', '_blank');
                    }}
                  >
                    Download Sample CSV
                  </Button>
                </div>
              </Col>
            )}
          </Row>
        </div>

        {/* Search Results Indicator */}
        {isSearchMode && activeSearchQuery.trim() && (
          <div className="search-results-indicator">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SearchOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
              <Text strong style={{ color: '#1890ff' }}>
                Search Results for &quot;{activeSearchQuery}&quot;
              </Text>
              <Text style={{ marginLeft: '8px', color: '#666' }}>
                ({totalCount} forms found)
              </Text>
            </div>
            <Button 
              size="small" 
              onClick={handleSearchClear}
              style={{ borderRadius: '6px' }}
            >
              Clear Search
            </Button>
          </div>
        )}
        
        <input
          id="csv-file-input"
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <FormLibraryCsvUpload
          open={modalOpen}
          mode={modalMode}
          initialFormData={modalInitialData}
          selectedFile={selectedFile}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
          isLoading={uploadLoading || addLoading}
          response={currentResponse}
          error={currentError}
        />
        {/* Edit Form modal for superadmin */}
        <FormLibraryCsvUpload
          open={editModalOpen}
          mode="edit"
          initialFormData={editInitialData}
          onClose={closeEditModal}
          onSubmit={handleEditModalSubmit}
          isLoading={updateLoading}
          response={updateResponse}
          error={updateError}
        />
        {/* Add to Library modal (parent controlled, only render when open) */}
        {/* {csvModalOpen && (
          <FormLibraryCsvUpload
            uploadCsvToLibrary={uploadCsvToLibrary}
            isLoading={uploadLoading}
            open={csvModalOpen}
            onClose={closeCsvModal}
            initialFormData={csvModalInitialData}
          />
        )} */}
        {/* Form Library Cards */}
        <div style={{ marginBottom: 24 }}>
          {libraryLoading || searchLoading ? (
            <div className="loading-container">
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text>{isSearchMode ? 'Searching forms...' : 'Loading forms...'}</Text>
              </div>
            </div>
          ) : libraryData.length === 0 ? (
            <div className="empty-state">
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {isSearchMode ? `No forms found matching &quot;${searchQuery}&quot;` : 'No forms found in the library'}
              </Text>
            </div>
          ) : (
            <Row gutter={[24, 24]}>
              {libraryData.map(record => renderFormCard(record))}
            </Row>
          )}
        </div>
        
        {/* Pagination */}
        {totalCount > libraryLimit && (
          <div className="pagination-container">
            <Pagination
              current={Math.floor(librarySkip / libraryLimit) + 1}
              pageSize={libraryLimit}
              total={totalCount}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} forms${isSearchMode ? ` matching &quot;${searchQuery}&quot;` : ''}`}
              onChange={(page, pageSize) => {
                setLibrarySkip((page - 1) * pageSize);
                setLibraryLimit(pageSize);
              }}
            />
          </div>
        )}
        {/* Client Access Modal */}
        <Modal
          title="Manage Client Access"
          open={accessModalOpen}
          onCancel={() => setAccessModalOpen(false)}
          onOk={handleSaveClientAccess}
          okText="Save"
          confirmLoading={clientAccessLoading}
          width={600}
        >
          {accessModalLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Client search bar */}
              <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                <Input
                  placeholder="Search clients by name..."
                  value={clientSearchInput}
                  onChange={handleClientSearchInputChange}
                  onKeyDown={handleClientSearchInputKeyDown}
                  allowClear
                  style={{ flex: 1 }}
                />
                <Button type="primary" onClick={handleClientSearch}>Search</Button>
              </div>
              <List
                key={selectedClients.join('-') + clientSearch}
                loading={clientListLoading}
                dataSource={clientListResponse?.data || []}
                renderItem={item => (
                  <List.Item>
                    <Checkbox
                      checked={selectedClients.includes(item.id)}
                      onChange={e => handleClientCheck(item.id, e.target.checked)}
                    >
                      <span style={{ fontWeight: 500 }}>{item.name || item.client_name || `Client #${item.id}`}</span>
                      <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>
                        (ID: {item.id}{item.client_code ? `, Code: ${item.client_code}` : ''})
                      </span>
                    </Checkbox>
                  </List.Item>
                )}
              />
              <Pagination
                current={clientPage}
                pageSize={clientPageSize}
                total={clientListResponse?.total_count || 0}
                onChange={page => setClientPage(page)}
                style={{ marginTop: 16, textAlign: 'right' }}
              />
            </>
          )}
        </Modal>

        {/* Form Details Modal */}
        <Modal
          title={
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ margin: 0 }}>📋 Form Details</Title>
              {selectedForm && (
                <Text type="secondary">{selectedForm.form_name}</Text>
              )}
            </div>
          }
          open={formDetailsModal}
          onCancel={closeFormDetails}
          footer={[
            <Button key="close" onClick={closeFormDetails}>
              Close
            </Button>,
            selectedForm && (
              <Button key="import" type="primary" onClick={() => handleImport(selectedForm)}>
                Import Form
              </Button>
            )
          ]}
          width="90vw"
          style={{ maxWidth: '1200px' }}
          bodyStyle={{
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '24px',
            position: 'relative'
          }}
          className="form-details-modal"
          centered
          destroyOnClose
          maskClosable={false}
        >
          {selectedForm && (
            <div style={{ padding: '16px 0' }}>
              {/* Basic Information */}
              <Card 
                title={
                  <Space>
                    <span>📝 Basic Information</span>
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Text strong>Form Name:</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text>{selectedForm.form_name}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text strong>Description:</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text>{selectedForm.form_description || 'No description available'}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text strong>Visibility:</Text>
                    <div style={{ marginTop: 4 }}>
                      <Tag color={selectedForm.is_public ? 'green' : 'orange'}>
                        {selectedForm.is_public ? 'Public' : 'Private'}
                      </Tag>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text strong>Created Date:</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text>{selectedForm.created_at ? new Date(selectedForm.created_at).toLocaleDateString() : 'N/A'}</Text>
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* Metadata Information */}
              <Card 
                title={
                  <Space>
                    <span>⚙️ Metadata</span>
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >
                <Row gutter={[16, 16]}>
                  {selectedForm.sku_code && (
                    <Col xs={24} sm={12} md={8}>
                      <Text strong>SKU Code:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text>{selectedForm.sku_code}</Text>
                      </div>
                    </Col>
                  )}
                  {selectedForm.form_type && (
                    <Col xs={24} sm={12} md={8}>
                      <Text strong>Form Type:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Tag color="blue">{selectedForm.form_type}</Tag>
                      </div>
                    </Col>
                  )}
                  {selectedForm.category && (
                    <Col xs={24} sm={12} md={8}>
                      <Text strong>Category:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text>{selectedForm.category}</Text>
                      </div>
                    </Col>
                  )}
                  {selectedForm.status && (
                    <Col xs={24} sm={12} md={8}>
                      <Text strong>Status:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Tag color={selectedForm.status === 'active' ? 'green' : 'red'}>
                          {selectedForm.status}
                        </Tag>
                      </div>
                    </Col>
                  )}
                  {selectedForm.logic_type && (
                    <Col xs={24} sm={12} md={8}>
                      <Text strong>Logic Type:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text>{selectedForm.logic_type}</Text>
                      </div>
                    </Col>
                  )}
                  {selectedForm.owned_by && (
                    <Col xs={24} sm={12} md={8}>
                      <Text strong>Owned By:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text>{selectedForm.owned_by}</Text>
                      </div>
                    </Col>
                  )}
                  {selectedForm.sample_report_link && (
                    <Col xs={24}>
                      <Text strong>Sample Report Link:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text>
                          <a href={selectedForm.sample_report_link} target="_blank" rel="noopener noreferrer">
                            {selectedForm.sample_report_link}
                          </a>
                        </Text>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>

              {/* Tags and Categories */}
              {(selectedForm.tags?.length > 0 || selectedForm.attributes?.length > 0 || selectedForm.target_audience?.length > 0) && (
                <Card 
                  title={
                    <Space>
                      <span>🏷️ Tags & Categories</span>
                    </Space>
                  }
                  style={{ marginBottom: 24 }}
                >
                  <Row gutter={[16, 16]}>
                    {selectedForm.tags?.length > 0 && (
                      <Col xs={24} sm={12}>
                        <Text strong>Tags:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                            {selectedForm.tags.map((tag, index) => (
                              <Tag key={index} color="blue">{tag}</Tag>
                            ))}
                          </Space>
                        </div>
                      </Col>
                    )}
                    {selectedForm.attributes?.length > 0 && (
                      <Col xs={24} sm={12}>
                        <Text strong>Attributes:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                            {selectedForm.attributes.map((attr, index) => (
                              <Tag key={index} color="orange">{attr}</Tag>
                            ))}
                          </Space>
                        </div>
                      </Col>
                    )}
                    {selectedForm.target_audience?.length > 0 && (
                      <Col xs={24}>
                        <Text strong>Target Audience:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                            {selectedForm.target_audience.map((audience, index) => (
                              <Tag key={index} color="purple">{audience}</Tag>
                            ))}
                          </Space>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card>
              )}

              {/* Pricing & Media Information */}
              {(selectedForm.credit_cost !== undefined || selectedForm.duration !== undefined || selectedForm.thumbnail) && (
                <Card 
                  title={
                    <Space>
                      <span>💰 Pricing & Media</span>
                    </Space>
                  }
                  style={{ marginBottom: 24 }}
                >
                  <Row gutter={[16, 16]}>
                    {selectedForm.credit_cost !== undefined && (
                      <Col xs={24} sm={12}>
                        <Text strong>Credit Cost:</Text>
                        <div style={{ marginTop: 4 }}>
                          <Text>{selectedForm.credit_cost} credits</Text>
                        </div>
                      </Col>
                    )}
                    {selectedForm.duration !== undefined && selectedForm.duration > 0 && (
                      <Col xs={24} sm={12}>
                        <Text strong>Duration:</Text>
                        <div style={{ marginTop: 4 }}>
                          <Text>{selectedForm.duration} minutes</Text>
                        </div>
                      </Col>
                    )}
                    {selectedForm.thumbnail && (
                      <Col xs={24} sm={12}>
                        <Text strong>Thumbnail:</Text>
                        <div style={{ marginTop: 4 }}>
                          <img 
                            src={selectedForm.thumbnail} 
                            alt="Form thumbnail" 
                            style={{ 
                              maxWidth: '100px', 
                              maxHeight: '100px', 
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }} 
                          />
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card>
              )}

              {/* Extra Metadata Information */}
              {selectedForm.extra_metadata && (
                <Card 
                  title={
                    <Space>
                      <span>🔧 Extra Metadata</span>
                    </Space>
                  }
                  style={{ marginBottom: 24 }}
                >
                  <Row gutter={[16, 16]}>
                    {selectedForm.extra_metadata.ai_prompt && (
                      <Col xs={24}>
                        <Text strong>AI Prompt:</Text>
                        <div style={{ marginTop: 4 }}>
                          <Text>{selectedForm.extra_metadata.ai_prompt}</Text>
                        </div>
                      </Col>
                    )}
                    {/* {selectedForm.extra_metadata.report_configs && Object.keys(selectedForm.extra_metadata.report_configs).length > 0 && (
                      <Col xs={24} sm={12}>
                        <Text strong>Report Configs:</Text>
                        <div style={{ marginTop: 4 }}>
                          <pre style={{ 
                            background: '#f5f5f5', 
                            padding: '8px', 
                            borderRadius: '4px',
                            fontSize: '12px',
                            overflow: 'auto'
                          }}>
                            {JSON.stringify(selectedForm.extra_metadata.report_configs, null, 2)}
                          </pre>
                        </div>
                      </Col>
                    )}
                    {selectedForm.extra_metadata.additional_data && Object.keys(selectedForm.extra_metadata.additional_data).length > 0 && (
                      <Col xs={24} sm={12}>
                        <Text strong>Additional Data:</Text>
                        <div style={{ marginTop: 4 }}>
                          <pre style={{ 
                            background: '#f5f5f5', 
                            padding: '8px', 
                            borderRadius: '4px',
                            fontSize: '12px',
                            overflow: 'auto'
                          }}>
                            {JSON.stringify(selectedForm.extra_metadata.additional_data, null, 2)}
                          </pre>
                        </div>
                      </Col>
                    )} */}
                  </Row>
                </Card>
              )}

              {/* Additional Information */}
              {(selectedForm.instructions || selectedForm.notes) && (
                <Card 
                  title={
                    <Space>
                      <span>📄 Additional Information</span>
                    </Space>
                  }
                >
                  <Row gutter={[16, 16]}>
                    {selectedForm.instructions && (
                      <Col xs={24}>
                        <Text strong>Instructions:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Text>{selectedForm.instructions}</Text>
                        </div>
                      </Col>
                    )}
                    {selectedForm.notes && (
                      <Col xs={24}>
                        <Text strong>Notes:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Text>{selectedForm.notes}</Text>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card>
              )}
            </div>
          )}
        </Modal>
      </Main>
    </>
  );
};

export default FormLibrary; 